import React from 'react';
import ReactDOM from 'react-dom/client';
import { Buffer } from 'buffer';
import { Amplify, Auth } from 'aws-amplify';

// Polyfill Buffer for aws-rum-web v3 signing (webpack 5 drops Node.js built-ins)
globalThis.Buffer = Buffer;
import awsExports from './aws-exports';
import '@aws-amplify/ui-react/styles.css';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { store } from './redux/store';
import { Provider } from 'react-redux';
import './i18n';
import { CustomThemeProvider } from './themes/ThemeContext';
import { initRum } from './rum';
import { cssBackgroundFlag } from './utils';
import { fetchFeatureFlags } from './redux/slices/featureFlags';
import { fetchSecuritySettings } from './redux/slices/security';
import { bootstrapSecurity } from './security/bootstrap';
import { reportPolicyViolations } from './security/violationReporting';
import { prefetchPartnerBranding } from './hooks/usePartnerBranding';

// Before Amplify: the token store has to be chosen before the Auth module is
// configured, and a policy is worth most before anything has been fetched.
// Both run off the settings cached by the previous load — see
// src/security/bootstrap.ts for why that trade is the right way round.
const security = bootstrapSecurity(awsExports as unknown as Record<string, unknown>);
// Registered straight after, so a policy that refuses something during boot is
// still recorded. There is no report-only mode for a meta policy, so this is the
// only warning a too-tight policy gives before a user notices.
reportPolicyViolations();

Amplify.configure(awsExports);
// Left alone in the default mode, so an unconfigured environment behaves exactly
// as it did before any of this existed.
if (security.storage) {
	Auth.configure({ storage: security.storage });
}
initRum();

// Pre-fetch feature flags via the guest-accessible endpoint so they are
// available before any component mounts (e.g. the signup stepper in Login).
store.dispatch(fetchFeatureFlags());

// Refreshes the cache the bootstrap above will read next time, and applies a
// policy on a browser whose cache had not yet heard about one.
store.dispatch(fetchSecuritySettings());

// Kick off partner-branding (logo) loading immediately, in parallel with feature
// flags, so a co-branded auth screen (`?org=<slug>`) shows the partner logo
// without the post-mount lazy-fetch delay. No-op when no `?org=` slug is present.
prefetchPartnerBranding();

if (cssBackgroundFlag) {
	document.body.classList.add('bg-css');
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
	<Provider store={store}>
		<CustomThemeProvider>
			<App />
		</CustomThemeProvider>
	</Provider>
);

reportWebVitals();
